import React, { useState, useEffect } from "react";
import useNotificationsService from "../../../../services/notificationService";
import { Bell, Check, Trash2, Filter, Clock } from "lucide-react";
import { useLanguage } from "../../../../context/LanguageContext";
import dashboardContent from "../../../../utils/dashboardContent";
import { message } from "antd";

const NotificationPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, unread, read
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const { getUserNotifications, markNotificationsAsRead } =
    useNotificationsService();
  const { language } = useLanguage();
  const text = dashboardContent[language].notifications;

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const res = await getUserNotifications();
      setNotifications(res);
    } catch (error) {
      console.error("Fehler beim Laden der Benachrichtigungen:", error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await markNotificationsAsRead(notificationId);
      fetchNotifications();
    } catch (error) {
      console.error("Fehler beim Markieren als gelesen:", error);
    }
  };

  const handleSelectNotification = (notificationId) => {
    setSelectedNotifications((prev) =>
      prev.includes(notificationId)
        ? prev.filter((id) => id !== notificationId)
        : [...prev, notificationId]
    );
  };

  const markSelectedAsRead = async () => {
    try {
      await Promise.all(
        selectedNotifications.map((id) => markNotificationsAsRead(id, false))
      );
      message.success(text.markSuccessAll);
      setSelectedNotifications([]);
      fetchNotifications();
    } catch (error) {
      console.error("Fehler beim Markieren als gelesen:", error);
    }
  };

  const getFormattedDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const filteredNotifications = notifications.filter((notification) => {
    if (filter === "unread") return !notification.read;
    if (filter === "read") return notification.read;
    return true;
  });

  const getNotificationTypeIcon = (type) => {
    switch (type) {
      case "feedback":
        return "💬";
      case "system":
        return "🔧";
      case "alert":
        return "⚠️";
      default:
        return "📌";
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">{text.title}</h1>
        <div className="flex gap-4">
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-gray-500" />
            <select
              className="rounded-lg border border-gray-300 bg-white px-3 py-2"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="all">{text.filterAll}</option>
              <option value="unread">{text.filterUnread}</option>
              <option value="read">{text.filterRead}</option>
            </select>
          </div>
          {selectedNotifications.length > 0 && (
            <button
              onClick={markSelectedAsRead}
              className="flex items-center gap-2 rounded-lg bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
            >
              <Check className="h-5 w-5" />
              {text.markSelectedAsRead}
            </button>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white">
        {filteredNotifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Bell className="h-12 w-12 text-gray-400" />
            <p className="mt-4 text-gray-500">{text.noNotifications}</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredNotifications.map((notification) => (
              <div
                key={notification._id}
                className={`flex items-start gap-4 p-4 hover:bg-gray-50 ${
                  !notification.read ? "bg-blue-50" : ""
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedNotifications.includes(notification._id)}
                  onChange={() => handleSelectNotification(notification._id)}
                  className="mt-1 h-4 w-4 rounded border-gray-300"
                />
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">
                        {getNotificationTypeIcon(notification.type)}
                      </span>
                      <p className="font-medium">{notification.message}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">
                        <Clock className="mr-1 inline-block h-4 w-4" />
                        {getFormattedDate(notification.createdAt)}
                      </span>
                      {!notification.read && (
                        <button
                          onClick={() => markAsRead(notification._id)}
                          className="rounded p-1 hover:bg-gray-100"
                          title={text.markAsRead}
                        >
                          <Check className="h-5 w-5 text-blue-500" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationPage;
